import Container, { Service } from 'typedi'
import HttpClient from '@/modules/common/services/http-client.service'
import Notification from '@/modules/common/services/notification.service'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'

interface ScormPingResponse {
  status: 'ok' | 'fail';
  companyId: number;
  message: string;
  apiMessage?: string;
  currentTime?: string;
}

interface UploadResult {
  success: boolean;
  jobId: string | null;
}

interface JobStatusResponse {
  status: 'COMPLETE' | 'ERROR' | 'FAILED' | 'PENDING' | 'RUNNING';
  message?: string;
  importResult?: {
    message?: string;
  };
}

@Service()
export default class ScormService {
  private notification = Container.get(Notification)
  private errorHandler = Container.get(ErrorHandlerService)

  public async ping (companyUuid: string): Promise<boolean> {
    try {
      const response = await Container.get(HttpClient).post<ScormPingResponse>({
        url: `company/${companyUuid}/scorm-cloud/ping`,
        requireToken: true
      })
      return response.status === 200 && response.data.status === 'ok'
    } catch {
      return false
    }
  }

  // --- API 1: Upload and Job Dispatch (Non-blocking) ---
  /**
   * Upload SCORM ZIP and initiate the import job on SCORM Cloud.
   * Returns the job ID for the frontend to start polling.
   */
  public async uploadScorm (companyUuid: string, file: File, courseId: string): Promise<UploadResult> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('courseId', courseId)

      const uploadResponse: any = await Container.get(HttpClient).post({
        url: `company/${companyUuid}/scorm-cloud/upload`,
        requireToken: true,
        payload: formData,
        isFormData: true
      })

      const rusticiJobId =
        uploadResponse.data?.rawResponse?.rustici_job_id ||
        uploadResponse.data?.course?.jobId

      if (!rusticiJobId) {
        this.notification.error('SCORM upload failed: No job ID returned.')
        return { success: false, jobId: null }
      }
      return { success: true, jobId: rusticiJobId }
    } catch (error) {
      this.errorHandler.error('SCORM upload failed.')
      return { success: false, jobId: null }
    }
  }

  // --- API 2: Single Job Status Check (Used by the frontend polling loop) ---
  /**
   * Checks the status of a Rustici import job one time.
   * NOTE: The frontend (gtr-event-creation-wizard) handles the looping/polling interval.
   */
  public async getJobStatus (companyUuid: string, rusticiJobId: string): Promise<JobStatusResponse> {
    const response: any = await Container.get(HttpClient).get({
      url: `company/${companyUuid}/scorm-cloud/job-status/${rusticiJobId}`,
      requireToken: true
    })

    // Check for nested status structure if necessary, or assume top-level status
    const status = response.data?.status || 'ERROR'

    // Return the simplified status object
    return {
      status: status,
      message: response.data?.message,
      importResult: response.data?.importResult
    }
  }
}
